import { FieldBlock, FieldType, TextStyle } from '@coast/schemas';
import { ReactElement, useContext, useMemo } from 'react';
import { View } from '@shared/components/View';
import { useLayoutConfig } from '@shared/components/blocks/contexts/layoutConfig';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { CardThemeContext } from '@shared/components/blocks/contexts/theme';
import { MBBlockLabel } from '@shared/components/blocks/utils/label';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext } from '@shared/styles';
import { defaultFieldPresenter } from './defaultFieldPresenter';
import { renderIcon } from './input';

interface Props {
  value: any;
  block: FieldBlock;
  onPress?(): void;
  presenter?(
    value: any,
    fieldType: FieldType | undefined,
    textStyle: TextStyle | undefined,
    textColor: string,
  ): ReactElement;
  renderLeft?(): ReactElement;
}

export function MBFieldBlock({
  value,
  block,
  onPress,
  presenter = defaultFieldPresenter,
  renderLeft,
}: Props) {
  const stale = useContext(CardThemeContext) === 'stale';
  const negative = typeof value === 'number' && value < 0;
  const colors = useContext(ColorThemeContext);
  const renderContext = useContext(CardRenderContext);
  const {
    staticBlock: { paddingLeft, displayLabel },
  } = useLayoutConfig();
  const textColor = negative
    ? colors.error.foreground
    : stale
    ? colors.foregroundInactive
    : colors.foregroundActive;
  const content = useMemo(
    () => presenter(value, block.fieldType, block.textStyle, textColor),
    [value, block.fieldType, block.textStyle, presenter, textColor],
  );

  const leftContent = useMemo((): ReactElement => {
    if (renderLeft) {
      return renderLeft();
    }
    return renderIcon(block.icon, block.name);
  }, [renderLeft, block]);
  const label = useMemo(() => {
    const shouldDisplayLabel = !!block.label && displayLabel;
    return shouldDisplayLabel ? <MBBlockLabel label={block.label} /> : null;
  }, [block, displayLabel]);

  const isCollectionView = ['list', 'table'].includes(renderContext);
  let iconPad = isCollectionView ? 1 : 2;
  if (renderContext === 'calendar') {
    iconPad = 0;
  }

  return (
    <MBTouchableOpacity activeOpacity={1} onPress={onPress}>
      <View row paddingLeft={paddingLeft} paddingRight={4} paddingVertical={!!label ? 0.5 : 0}>
        {!!leftContent && leftContent}
        {!!leftContent && <View paddingRight={iconPad} />}
        <View flex centerVertically>
          {label}
          {content}
        </View>
      </View>
    </MBTouchableOpacity>
  );
}
